import { supabase } from "@/lib/supabaseClient";

/**
 * Cliente para la Edge Function "ghl": un proxy seguro hacia la API de
 * GoHighLevel. El token de GHL vive únicamente en Supabase Vault y nunca
 * llega al navegador -- esta función solo habla con nuestra propia Edge
 * Function usando la sesión normal de Supabase del usuario.
 */

export type GhlOverview = {
  contactId: string;
  overview: {
    habitsStreak: string | null;
    habitsCompletedToday: string | null;
    habitsLastCheckin: string | null;
    diaryStreak: string | null;
    diaryLastEntry: string | null;
    coachWeeklySummary: string | null;
    coachSummaryDate: string | null;
    lastClassId: string | null;
    lastClassTitle: string | null;
    lastClassPercent: string | null;
    lastClassResumeLink: string | null;
    lastClassUpdatedAt: string | null;
    lastCourseCompleted: string | null;
    lastTestCompleted: string | null;
  };
  courses: GhlCourseProgress[];
  coursesProgressAvg: number | null;
};

export type GhlCourseProgress = {
  courseId: string;
  classTitle: string;
  percent: number;
  contactId: string;
};

export type GhlOverviewResult =
  | { ok: true; data: GhlOverview }
  | { ok: false; reason: "no_contact" | "error"; message?: string };

/**
 * Trae el resumen de progreso real del contacto de GHL asociado al usuario
 * autenticado (racha de hábitos, última clase, progreso por curso, etc).
 * Si el usuario aún no tiene un contacto de GHL vinculado (por correo),
 * devuelve { ok: false, reason: "no_contact" } -- no es un error fatal,
 * simplemente significa que todavía no hay datos reales que mostrar.
 */
export async function fetchGhlOverview(): Promise<GhlOverviewResult> {
  try {
    const { data, error } = await supabase.functions.invoke("ghl", {
      body: { action: "overview" },
    });

    if (error) {
      console.error("fetchGhlOverview invoke error", error);
      return { ok: false, reason: "error", message: error.message };
    }

    if (data?.error === "no_ghl_contact") {
      return { ok: false, reason: "no_contact", message: data.message };
    }

    if (data?.error) {
      console.error("fetchGhlOverview function error", data.error, data.detail);
      return { ok: false, reason: "error", message: data.message ?? data.error };
    }

    return { ok: true, data: data as GhlOverview };
  } catch (err) {
    console.error("fetchGhlOverview threw", err);
    return { ok: false, reason: "error", message: String(err) };
  }
}

export type GhlMeetingEvent = {
  id?: string;
  title?: string;
  name?: string;
  startTime?: string;
  endTime?: string;
  address?: string;
  appointmentStatus?: string;
  [key: string]: unknown;
};

/**
 * Trae los próximos eventos del Calendar de GHL (próximos 30 días).
 * Requiere el scope "calendars/events.readonly" en la Private Integration
 * de GHL -- si ese scope no está habilitado, la función devuelve [] en
 * lugar de fallar, para que la pantalla de Reuniones simplemente muestre
 * un estado vacío en vez de romperse.
 */
export async function fetchGhlMeetings(): Promise<GhlMeetingEvent[]> {
  try {
    const { data, error } = await supabase.functions.invoke("ghl", {
      body: { action: "meetings" },
    });
    if (error) {
      console.error("fetchGhlMeetings invoke error", error);
      return [];
    }
    if (data?.error) {
      console.warn("fetchGhlMeetings function error", data.error);
      return [];
    }
    return (data?.events ?? []) as GhlMeetingEvent[];
  } catch (err) {
    console.error("fetchGhlMeetings threw", err);
    return [];
  }
}
